import { ReactElement } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { IconArrayObjects } from '@components/icons/array-objects';
import { IconArrayStrings } from '@components/icons/array-strings';
import { IconBoolean } from '@components/icons/boolean';
import { IconDate } from '@components/icons/date';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconEmail } from '@components/icons/email';
import { IconFile } from '@components/icons/file';
import { IconLock } from '@components/icons/lock';
import { IconPhone } from '@components/icons/phone';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { IntegrationEndpointVariable } from './integrations-types';

const IconColor = 'elementPurple50';

export const TYPE_ICON_MAP: {
  [Key in IntegrationEndpointVariable['type']]: ReactElement<IconProps>;
} = {
  webhookUrl: <IconTextFormatLink color={IconColor} />,
  text: <IconTextShort color={IconColor} />,
  paragraph: <IconTextLong color={IconColor} />,
  number: <IconTextFormatNumber color={IconColor} />,
  decimal: <IconDecimalFloat color={IconColor} />,
  select: <IconSelectSingle color={IconColor} />,
  file: <IconFile color={IconColor} />,
  date: <IconDate color={IconColor} />,
  email: <IconEmail color={IconColor} />,
  phone: <IconPhone color={IconColor} />,
  array: <IconArrayObjects color={IconColor} />,
  array_of_strings: <IconArrayStrings color={IconColor} />,
  boolean: <IconBoolean color={IconColor} />,
  secret: <IconLock color={IconColor} />,
};

export const Grid = styled.div<{ $numColumns?: number; $numSmallColumns?: number }>(
  ({ $numColumns = 4, $numSmallColumns = 0 }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${$numColumns}, minmax(100px, 1fr)) ${
      $numSmallColumns !== 0 ? `repeat(${$numSmallColumns}, minmax(56px, 56px))` : ''
    }`,
    [`${Field}:nth-last-child(-n+${$numColumns + $numSmallColumns})`]: {
      border: 'none',
    },
  })
);

const FIELD_PADDING = 8;
const FIELD_BORDER = 1;

const INDENT_BORDER = 4;
const INDENT_BORDER_GAP = 16;
const INDENT_BORDER_TOTAL = INDENT_BORDER + INDENT_BORDER_GAP;

const getTotalIndent = (depth: number) => FIELD_PADDING + INDENT_BORDER_TOTAL * depth;

export const FieldIndentBorderContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
})<{ $depth?: number }>(({ $depth = 0 }) => ({
  '&&': {
    width: 'inherit',
  },
  gap: pxToRem(INDENT_BORDER_GAP),
  // height: 100% + padding top + padding bottom + border width
  height: `calc(100% + ${pxToRem(FIELD_PADDING * 2 + FIELD_BORDER)})`,
  // margin: move the padding from the left side to the right side of the borders and and also remove extra width based on number of borders
  marginRight: pxToRem(FIELD_PADDING),
  marginLeft: `-${pxToRem(getTotalIndent($depth))}`,
}));

export const FieldIndentBorder = styled.div<{
  $color: string;
  $isFirst?: boolean;
  $isLast?: boolean;
}>(({ $color, $isFirst, $isLast }) => ({
  '&&': {
    width: pxToRem(INDENT_BORDER),
  },
  height: '100%',
  backgroundColor: $color,

  ...($isFirst
    ? {
        borderTopLeftRadius: pxToRem(2),
        borderTopRightRadius: pxToRem(2),
        // margin: have both paddings on top so the line starts lower but ends at the bottom
        marginTop: pxToRem(FIELD_PADDING * 2),
        // height: remove the extra height added in the container and only have the original height
        height: `calc(100% - ${pxToRem(FIELD_PADDING * 2)})`,
      }
    : {}),
  ...($isLast
    ? {
        borderBottomLeftRadius: pxToRem(2),
        borderBottomRightRadius: pxToRem(2),
        // margin: have both paddings on bottom so the line ends lower but starts at the top
        marginBottom: pxToRem(FIELD_PADDING * 2),
        // height: remove the extra height added in the container and only have the original height
        height: `calc(100% - ${pxToRem(FIELD_PADDING * 2)})`,
      }
    : {}),

  ...($isFirst && $isLast
    ? {
        // if the item is the first and the last item, we need to remove both the height from the top and the bottom so that it's in the middle because the other classes overwrite each other
        height: `calc(100% - ${pxToRem(FIELD_PADDING * 4)})`,
      }
    : {}),
}));

export const Field = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'start',
})<{ $depth?: number }>(({ $depth }) => ({
  '> div': {
    width: '100%',
  },
  padding: pxToRem(FIELD_PADDING),
  borderBottom: `1px solid ${theme.color.uiTrans10} `,

  ...($depth !== undefined
    ? {
        // Remove the padding because it needs to be a margin because of the border
        paddingLeft: pxToRem(0),
        // Add the margin that was removed in the border container and than add the padding removed above
        marginLeft: pxToRem(FIELD_PADDING + getTotalIndent($depth)),
      }
    : {}),

  minHeight: pxToRem(56),
}));
